// One editable artifact card, shared by Review and Edit. Leads with the tour-tag
// editor (membership is the field touched on every review page; DESIGN §9), then
// shows the original photo + candidate canonical image, the progressive-disclosure
// ArtifactForm, image provenance/fidelity, a "Take a better picture" recapture for
// placeholders, and a configurable primary action (Confirm in Review, Save in Edit)
// plus Delete and (Edit only) Re-run enrichment. CONTRACTS §10.
//
// Two later refinements (continuation 2026-06-07):
//   - P1b "re-attach original photo": if an artifact's original.jpg 404s we offer a
//     capture→crop→reupload that restores ONLY the photo bytes (no re-enrich), so a
//     missing original doesn't strand an otherwise-correct record.
//   - P2 "top-5 reverse-image picker": when the match came from reverse-image search
//     and several candidates were stored, a cycler lets the reviewer pick the right
//     one instead of being stuck with the auto-chosen (often wrong) first hit.
//   - P2 "general items": kind==="general" records skip the identity/enrichment
//     happy path, so the card hides all of that chrome and just shows the photo,
//     tour/general tags, and freeform notes.

import { useState } from "react";
import type { Artifact } from "../types";
import { useArtifactDraft } from "../draft";
import { artifactImageUrls, isPlaceholder } from "../artifactImages";
import {
  ApiError,
  deleteArtifact,
  enrich,
  updateArtifact,
  useOriginal,
  useReverseCandidate,
} from "../api";
import { ArtifactForm } from "./ArtifactForm";
import { ArtImage } from "./ArtImage";
import { EnrichmentSummary } from "./EnrichmentSummary";
import { RecaptureModal } from "./RecaptureModal";
import { TourTagSection } from "./TourTagSection";

export interface ArtifactCardProps {
  artifact: Artifact;
  /** Label for the primary action button (e.g. "Confirm", "Save changes"). */
  primaryLabel: string;
  /** Status to set on the primary action (Review -> "confirmed"); omit to just save edits. */
  primaryStatus?: Artifact["status"];
  /** Show the "Re-run enrichment" button (Edit pane). */
  allowReenrich?: boolean;
  /** Distinct tour slugs across the store (one-tap reuse chips). Review pane. */
  existingTourTags?: string[];
  /** Session-sticky most-recently-applied tour slug (prominent reapply). */
  lastAppliedTour?: string | null;
  /** Notify the pane that a tour slug was applied (updates the sticky default). */
  onTourApplied?: (slug: string) => void;
  /** Called after a successful save/confirm with the updated artifact. */
  onSaved: (a: Artifact) => void;
  /** Called after a successful delete. */
  onDeleted: (id: string) => void;
  /** Called after a recapture/re-enrich is triggered (so the parent can refresh). */
  onReenriched: (id: string) => void;
}

export function ArtifactCard({
  artifact,
  primaryLabel,
  primaryStatus,
  allowReenrich,
  existingTourTags,
  lastAppliedTour,
  onTourApplied,
  onSaved,
  onDeleted,
  onReenriched,
}: ArtifactCardProps) {
  const d = useArtifactDraft(artifact);
  const [busy, setBusy] = useState<null | "save" | "delete" | "enrich" | "useOriginal">(null);
  const [error, setError] = useState<string | null>(null);
  const [recapturing, setRecapturing] = useState(false);
  // P1b: the original photo can't be displayed (its <id>/original.jpg 404s). We
  // then offer a re-attach that restores ONLY the bytes (no re-enrich).
  const [originalBroken, setOriginalBroken] = useState(false);
  const [reattaching, setReattaching] = useState(false);
  // Bumped after a successful re-attach to cache-bust the original URL so the
  // browser fetches the fresh bytes instead of the cached 404.
  const [originalBust, setOriginalBust] = useState(0);

  const general = artifact.kind === "general";

  const urls = artifactImageUrls(artifact);
  const placeholder = isPlaceholder(artifact);
  // A reverse-image match is low-trust (the web image may be the wrong work):
  // offer a one-tap revert to the original photo (continuation 2026-06-07e). Never
  // for general items (they have no online identity at all).
  const reverseMatch = !general && artifact.imageSource === "reverse" && !placeholder;
  // Reverse-image candidates are now offered as an escape hatch in EVERY case
  // (continuation 2026-06-07j) — even an NGA/Met match, which can be a reproduction
  // (a lithograph OF the work) rather than the work itself. So the cycler shows
  // whenever candidates exist; the structured match stays the DEFAULT image and the
  // cycler is collapsed by default for it (open by default only for a reverse pick).
  const candidateCount = general ? 0 : (artifact.reverseImageCandidates?.length ?? 0);
  const showCandidateCycler = reverseMatch ? candidateCount > 1 : candidateCount >= 1;

  // Cache-busted original URL: appends ?t= after a re-attach so fresh bytes load.
  // Browser code, so Date.now()/a counter is fine.
  const bustOriginal = (u: string | undefined): string | undefined =>
    u && originalBust ? `${u}${u.includes("?") ? "&" : "?"}t=${originalBust}` : u;
  const originalUrl = bustOriginal(urls.original);
  // The canonical figure falls back to the (busted) original too.
  const canonicalDisplay = urls.canonical ?? originalUrl;

  const errMsg = (err: unknown, fallback: string) =>
    err instanceof ApiError ? err.message : err instanceof Error ? err.message : fallback;

  const save = async () => {
    setBusy("save");
    setError(null);
    try {
      const patch = { ...d.diff(), ...(primaryStatus ? { status: primaryStatus } : {}) };
      const updated = await updateArtifact(artifact.id, patch);
      d.reset(updated);
      onSaved(updated);
    } catch (err) {
      setError(errMsg(err, "Save failed."));
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async () => {
    if (!confirm("Delete this artifact and its images? This cannot be undone.")) return;
    setBusy("delete");
    setError(null);
    try {
      await deleteArtifact(artifact.id);
      onDeleted(artifact.id);
    } catch (err) {
      setError(errMsg(err, "Delete failed."));
      setBusy(null);
    }
  };

  const onReenrich = async () => {
    if (!confirm("Re-run enrichment? This re-derives metadata/images (your manual edits are preserved).")) {
      return;
    }
    setBusy("enrich");
    setError(null);
    try {
      await enrich(artifact.id);
      onReenriched(artifact.id);
    } catch (err) {
      setError(errMsg(err, "Could not trigger enrichment."));
    } finally {
      setBusy(null);
    }
  };

  const onUseOriginal = async () => {
    if (
      !confirm(
        "Discard the online image and use your photo instead? The found image " +
          "looks like the wrong artwork. Your original photo is kept, and you can " +
          "re-run enrichment or take a better picture afterwards.",
      )
    ) {
      return;
    }
    setBusy("useOriginal");
    setError(null);
    try {
      // Only image fields change server-side; update the card WITHOUT resetting
      // the draft, so any in-progress text edits are preserved.
      const updated = await useOriginal(artifact.id);
      onSaved(updated);
    } catch (err) {
      setError(errMsg(err, "Could not revert to your photo."));
    } finally {
      setBusy(null);
    }
  };

  const titleText = artifact.title?.trim() || "Untitled / unidentified";

  return (
    <article className="card artifact-card">
      <header className="card__header">
        <h3 className="card__title">{titleText}</h3>
        {general ? (
          <span className="fidelity-pill fidelity-pill--general">general item · no lookup</span>
        ) : (
          <span className={`fidelity-pill fidelity-pill--${placeholder ? "placeholder" : "match"}`}>
            {placeholder ? "original only" : `online match${artifact.imageSource ? ` · ${artifact.imageSource}` : ""}`}
          </span>
        )}
      </header>

      {/* Tour membership leads the card: it's the field touched on every review
          page, with a sticky one-tap reapply + reuse chips (DESIGN §9). */}
      <TourTagSection
        d={d}
        idPrefix={artifact.id}
        existingTourTags={existingTourTags}
        lastAppliedTour={lastAppliedTour}
        onTourApplied={onTourApplied}
      />

      {/* What enrichment actually did — outcome, confidence, per-field sources,
          source-image link. Hidden for general items (no lookup ran). */}
      {!general && <EnrichmentSummary artifact={artifact} />}

      {!general && artifact.enrichmentError && (
        <p className="enrich-error">Enrichment error: {artifact.enrichmentError}</p>
      )}

      <div className="card__body">
        <div className="card__images">
          {general ? (
            // General item: just the photo, no candidate/online chrome.
            <figure className="image-figure">
              <ArtImage
                src={originalUrl}
                alt={`${titleText} (your photo)`}
                className="image-candidate"
                onBroken={() => setOriginalBroken(true)}
              />
              <figcaption>Your photo</figcaption>
            </figure>
          ) : (
            <>
              <figure className="image-figure">
                <ArtImage
                  src={canonicalDisplay}
                  fallbackSrc={originalUrl}
                  alt={`${titleText} (best available)`}
                  className="image-candidate"
                  // When there's no canonical, this IS the original photo, so a
                  // load failure here means the original is broken.
                  onBroken={urls.canonical ? undefined : () => setOriginalBroken(true)}
                />
                <figcaption>{urls.canonical ? "Candidate (online)" : "Original photo"}</figcaption>
              </figure>
              {urls.canonical && (
                <figure className="image-figure image-figure--small">
                  <ArtImage
                    src={originalUrl}
                    alt="Original photo"
                    className="image-original"
                    onBroken={() => setOriginalBroken(true)}
                  />
                  <figcaption>Your photo</figcaption>
                </figure>
              )}
            </>
          )}

          {/* P1b: the original photo failed to load — offer a re-attach that
              restores ONLY the bytes (no re-enrich). Shown for ANY item whose
              original is actually broken (not normal placeholders). */}
          {originalBroken && (
            <div className="reattach">
              <p className="reattach__note">This item's original photo is missing.</p>
              <button
                type="button"
                className="btn btn--block reattach__btn"
                onClick={() => setReattaching(true)}
                disabled={busy !== null}
              >
                Re-attach original photo
              </button>
            </div>
          )}

          {!general && placeholder && (
            <button
              type="button"
              className="btn btn--block"
              onClick={() => setRecapturing(true)}
            >
              Take a better picture
            </button>
          )}

          {/* P2: reverse-image candidate cycler — pick the right web image. Shown
              as an escape hatch in every case; open by default only for a reverse
              pick, collapsed for a structured match (which stays the default). */}
          {showCandidateCycler && (
            <ReverseCandidateCycler
              artifact={artifact}
              busy={busy !== null}
              defaultOpen={reverseMatch}
              onSaved={onSaved}
            />
          )}

          {reverseMatch && (
            <button
              type="button"
              className="btn btn--block"
              onClick={() => void onUseOriginal()}
              disabled={busy !== null}
              title="The online image may be the wrong artwork — revert to your photo"
            >
              {busy === "useOriginal" ? "Reverting…" : "Use my photo instead"}
            </button>
          )}
        </div>

        <div className="card__form">
          <ArtifactForm artifact={artifact} d={d} idPrefix={artifact.id} />
        </div>
      </div>

      {error && <p className="form-error">{error}</p>}

      <footer className="card__footer">
        <button
          type="button"
          className="btn btn--primary btn--large"
          onClick={() => void save()}
          disabled={busy !== null || (primaryStatus === undefined && !d.dirty)}
        >
          {busy === "save" ? "Saving…" : primaryLabel}
          {primaryStatus === undefined && d.dirty ? " *" : ""}
        </button>
        {allowReenrich && !general && (
          <button
            type="button"
            className="btn"
            onClick={() => void onReenrich()}
            disabled={busy !== null}
          >
            {busy === "enrich" ? "Re-enriching…" : "Re-run enrichment"}
          </button>
        )}
        <button
          type="button"
          className="btn btn--danger btn--ghost"
          onClick={() => void onDelete()}
          disabled={busy !== null}
        >
          {busy === "delete" ? "Deleting…" : "Delete"}
        </button>
      </footer>

      {recapturing && (
        <RecaptureModal
          artifactId={artifact.id}
          onClose={() => setRecapturing(false)}
          onRecaptured={(id) => {
            setRecapturing(false);
            // Same record, re-enriching on the new photo — just re-fetch.
            onReenriched(id);
          }}
        />
      )}

      {reattaching && (
        <RecaptureModal
          artifactId={artifact.id}
          // Original-only: restore the photo bytes WITHOUT re-running enrichment,
          // so the (correct) existing match/metadata is preserved.
          enrichAfter={false}
          onClose={() => setReattaching(false)}
          onRecaptured={(id) => {
            setReattaching(false);
            setOriginalBroken(false);
            // Cache-bust so the fresh original bytes load over the cached 404,
            // then refresh the card from the server.
            setOriginalBust(Date.now());
            onReenriched(id);
          }}
        />
      )}
    </article>
  );
}

/**
 * Reverse-image picker: cycle the stored candidate URLs and commit the right one
 * (the human pick IS the verification, so the server does a verify-free commit).
 *
 * Offered as an escape hatch in EVERY case (continuation 2026-06-07j), not just
 * for reverse matches: a structured NGA/Met match can be a reproduction (e.g. a
 * lithograph) rather than the work itself. For a structured match these are
 * ALTERNATIVES — the cycler starts collapsed (the match stays the default) behind
 * a "Prefer a different image?" toggle; for a reverse pick it opens directly and
 * starts on the candidate currently in use.
 */
function ReverseCandidateCycler({
  artifact,
  busy,
  defaultOpen,
  onSaved,
}: {
  artifact: Artifact;
  busy: boolean;
  defaultOpen: boolean;
  onSaved: (a: Artifact) => void;
}) {
  const candidates = artifact.reverseImageCandidates ?? [];
  const initialIndex = Math.max(
    0,
    candidates.findIndex((u) => u === artifact.sourceImageUrl),
  );
  const [index, setIndex] = useState(initialIndex);
  const [open, setOpen] = useState(defaultOpen);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const total = candidates.length;
  // The current canonical is itself a reverse pick (these ARE the candidates) vs.
  // a structured match (these are ALTERNATIVES to the default image).
  const isAlternative = artifact.imageSource !== "reverse";

  if (total === 0) return null;

  // Collapsed (structured match): a single unobtrusive toggle keeps the default
  // image clean while still exposing the escape hatch on every card.
  if (!open) {
    return (
      <button
        type="button"
        className="btn btn--block candidate-cycler__toggle"
        onClick={() => setOpen(true)}
        disabled={busy}
        title="Show reverse-image search results you can use instead"
      >
        Prefer a different image? ({total} option{total === 1 ? "" : "s"})
      </button>
    );
  }

  const current = candidates[index];
  const disabled = busy || committing;

  const go = (delta: number) => {
    setIndex((i) => (i + delta + total) % total);
  };

  const useThis = async () => {
    setCommitting(true);
    setError(null);
    try {
      const updated = await useReverseCandidate(artifact.id, index);
      onSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not set this image.");
    } finally {
      setCommitting(false);
    }
  };

  // Whether the on-screen candidate is the one already committed as canonical.
  const isCurrentCommitted = current === artifact.sourceImageUrl;

  return (
    <div className="candidate-cycler">
      <div className="candidate-cycler__head">
        <span className="candidate-cycler__count">
          {isAlternative ? "Alternative" : "Candidate"} {index + 1} of {total}
        </span>
        <div className="candidate-cycler__nav">
          <button
            type="button"
            className="btn btn--small"
            onClick={() => go(-1)}
            disabled={disabled || total < 2}
            aria-label="Previous candidate"
          >
            ‹ Prev
          </button>
          <button
            type="button"
            className="btn btn--small"
            onClick={() => go(1)}
            disabled={disabled || total < 2}
            aria-label="Next candidate"
          >
            Next ›
          </button>
          {isAlternative && (
            <button
              type="button"
              className="btn btn--small"
              onClick={() => setOpen(false)}
              disabled={disabled}
            >
              Hide
            </button>
          )}
        </div>
      </div>

      {isAlternative && (
        <p className="candidate-cycler__note">
          Reverse-image search results. Your current image stays unless you pick one.
        </p>
      )}

      <figure className="image-figure candidate-cycler__figure">
        <ArtImage
          src={current}
          alt={`Reverse-image candidate ${index + 1} of ${total}`}
          className="image-candidate"
        />
      </figure>

      <button
        type="button"
        className="btn btn--block btn--primary candidate-cycler__use"
        onClick={() => void useThis()}
        disabled={disabled || isCurrentCommitted}
      >
        {committing
          ? "Setting image…"
          : isCurrentCommitted
            ? "This image is in use"
            : "Use this image"}
      </button>
      {error && <p className="form-error">{error}</p>}
    </div>
  );
}
